import { useState, useEffect } from 'react';
import {
  TextInput,
  PasswordInput,
  Title,
  Button,
  Text,
  Alert,
  Stack,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { pb } from '../../lib/pocketbase';
import { RecordModel } from 'pocketbase';
import classes from './LoginPage.module.css';

interface LoginPageProps {
  onLogin: () => void;
  logoUrl?: string;
  appTitle?: string;
}

interface SettingsConfig {
  title: string;
  primaryColor: string;
}

interface SettingsRecord extends RecordModel {
  config: SettingsConfig;
  logo?: string;
  favicon?: string;
  image?: string;
}

export function LoginPage({ onLogin, appTitle = 'ASGS 2.1' }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<SettingsRecord | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const records = await pb.collection('settings').getList(1, 1);
        
        if (records.items.length > 0) {
          const record = records.items[0] as SettingsRecord;
          console.log('Login page fetched settings:', record);
          setSettings(record);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await pb.collection('users').authWithPassword(email, password);
      onLogin();
    } catch (err: any) {
      console.error('Login error:', err);
      setError('Login fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.');
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingSettings) {
    return (
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        Loading...
      </div>
    );
  }

  const imageUrl = settings?.image 
    ? `${pb.baseUrl}/api/files/${settings.collectionId}/${settings.id}/${settings.image}`
    : undefined;

  const logoUrl = settings?.logo
    ? `${pb.baseUrl}/api/files/${settings.collectionId}/${settings.id}/${settings.logo}`
    : undefined;

  return (
    <div className={classes.wrapper}>
      <div className={classes.container}>
        <div className={classes.form}>
          {logoUrl && (
            <Stack align="center" mb="xl">
              <img src={logoUrl} alt="Logo" style={{ maxHeight: '60px', width: 'auto' }} />
            </Stack>
          )}

          <Title order={2} className={classes.title} ta="center" mb={50}>
            {settings?.config.title || appTitle}
          </Title>

          <form onSubmit={handleSubmit}>
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
                {error}
              </Alert>
            )}
            
            <TextInput
              label="Email"
              placeholder="Ihre Email"
              size="md"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            
            <PasswordInput
              label="Passwort"
              placeholder="Ihr Passwort"
              size="md"
              required
              mt="md"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            
            <Button 
              fullWidth 
              mt="xl"
              size="md"
              type="submit"
              loading={loading}
              color={settings?.config.primaryColor}
            >
              Anmelden
            </Button>
          </form>

          <Text ta="center" mt="md">
            Noch kein Konto? Kontaktieren Sie Ihren Administrator
          </Text>
        </div>

        {imageUrl && (
          <div 
            className={classes.imageWrapper}
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
        )}
      </div>
    </div>
  );
}
